/**
 * Thin REST client for the ShelvesLab cloud API.
 *
 * The base URL is bound at build time. In dev (Bun --hot src/index.ts) it
 * falls back to http://localhost:3000. In production it is baked in via
 * build.ts using the SHELVESLAB_API env var.
 *
 * Runtime override is still supported: set window.__SHELVESLAB_API__ before
 * the bundle loads (useful for smoke-testing prod bundles against a local
 * cloud).
 */

declare const __SHELVESLAB_API__: string | undefined;
declare global {
  interface Window {
    __SHELVESLAB_API__?: string;
  }
}

const BUILD_TIME_BASE: string =
  // Replaced literally by build.ts when SHELVESLAB_API is set.
  // The unreplaced fallback is fine for dev (Bun --hot).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (typeof __SHELVESLAB_API__ !== "undefined" ? (__SHELVESLAB_API__ as any) : null) ||
  "http://localhost:3000";

export const API_BASE: string =
  (typeof window !== "undefined" && window.__SHELVESLAB_API__) || BUILD_TIME_BASE;

export type TagAggregate = {
  tag_id: string;
  count: number;
  avg_confidence: number;
};

export type AggregateRow = {
  id: number;
  device_id: string;
  planogram_id: string;
  planogram_version: string;
  taxonomy_version: string;
  firmware_version: string;
  tenant_key: string;
  retailer: string;
  bucket_start: string;
  bucket_end: string;
  k_threshold: number;
  ingested_at: string;
  tags: { tag_id: string; count: number; avg_confidence: number }[];
};

export type TopTag = {
  tag_id: string;
  total_count: number;
  avg_confidence: number;
};

export type BusEvent = {
  type: "aggregate.ingested";
  tenant_key: string;
  payload: {
    device_id: string;
    planogram_id: string;
    firmware_version: string;
    bucket_start: string;
    bucket_end: string;
    k_threshold: number;
    tags: TagAggregate[];
  };
  ingested_at: string;
};

async function get<T>(path: string): Promise<T> {
  const r = await fetch(`${API_BASE}${path}`);
  if (!r.ok) throw new Error(`${r.status} ${path}`);
  return r.json() as Promise<T>;
}

export const api = {
  health: () => get<{ ok: boolean; service: string; version: string }>("/health"),
  stats:  () => get<{ aggregates: number; tags: number; tenants: number }>("/api/stats"),
  tenants: () => get<{ tenants: string[] }>("/api/tenants"),
  topTags: (tenantKey: string, sinceIso?: string) =>
    get<{ tenant_key: string; since: string; until: string; tags: TopTag[] }>(
      `/api/top-tags?tenant_key=${encodeURIComponent(tenantKey)}` +
      (sinceIso ? `&since=${encodeURIComponent(sinceIso)}` : ""),
    ),
  aggregates: (tenantKey: string, sinceIso?: string) =>
    get<{ tenant_key: string; since: string; until: string; aggregates: AggregateRow[] }>(
      `/api/aggregates?tenant_key=${encodeURIComponent(tenantKey)}` +
      (sinceIso ? `&since=${encodeURIComponent(sinceIso)}` : ""),
    ),
};
